import type { ControllerRenderProps } from "react-hook-form";

import { Info } from "lucide-react";
import useAdminUpsertQuestion from "src/features/Admin/Observations/hooks/useAdminUpsertQuestion/useAdminUpsertQuestion";

type AdminCCEmailRecipientBlockProps = {
	questionIndex: number;

	question: ControllerRenderProps<
		AdminObservationForm,
		`questions.${number}.question`
	>;

	note: ControllerRenderProps<AdminObservationForm, `questions.${number}.note`>;

	isActionRequired: ControllerRenderProps<
		AdminObservationForm,
		`questions.${number}.isActionRequired`
	>;

	isSettingsDisabled?: boolean;

	errors?: {
		question?: string;
	};
};

const AdminCCEmailRecipientBlock = ({
	question,
	note,
	questionIndex,
	isSettingsDisabled,
	errors,
	isActionRequired
}: AdminCCEmailRecipientBlockProps) => {
	const { upsertQuestion, isSubmittingQuestion, isUpdatingQuestion } =
		useAdminUpsertQuestion({ questionIndex });

	const isDisabled = isSubmittingQuestion || isUpdatingQuestion;
	const isLoading = isSubmittingQuestion || isUpdatingQuestion;

	const handleBlur = async (onBlur: () => void) => {
		try {
			await upsertQuestion();
		} catch (error) {
			console.error("Error creating/updating question", error);
		} finally {
			onBlur();
		}
	};

	return (
		<div>
			<h4 className="font-heading text-lg font-medium text-foreground mb-6">
				CC Email recipient
			</h4>

			<div className="flex flex-col gap-4">
				<div>
					<div className="flex justify-between mb-1">
						<label className="text-sm font-medium text-zinc-600 mb-1 block">
							Label
						</label>
						<span className="text-[13px] text-muted-foreground">Required</span>
					</div>

					<div className="relative">
						<input
							type="text"
							className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
							placeholder="Please enter label text"
							name={question.name}
							onChange={question.onChange}
							onBlur={() => handleBlur(question.onBlur)}
							value={question.value}
							ref={question.ref}
							disabled={isDisabled}
							autoFocus={true}
						/>

						{isLoading && (
							<span className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
						)}
					</div>

					{errors?.question && (
						<p className="text-destructive mt-1 text-xs flex items-center gap-1">
							<Info size={16} />
							{errors.question}
						</p>
					)}
				</div>

				<div>
					<label className="text-sm font-medium text-zinc-600 mb-1 block">
						Note
					</label>

					<input
						type="text"
						className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
						placeholder="Add any additional information to support this question"
						name={note.name}
						onChange={note.onChange}
						onBlur={() => handleBlur(note.onBlur)}
						value={note.value}
						ref={note.ref}
						disabled={isDisabled}
					/>
				</div>
			</div>

			<div className="pt-6 mt-6 border-t border-border">
				<p className="text-sm font-medium text-zinc-600 mb-4">Settings</p>

				<label
					className={`flex items-center gap-2 ${isSettingsDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
				>
					<input
						type="checkbox"
						checked={Boolean(isActionRequired.value)}
						onChange={isActionRequired.onChange}
						disabled={isSettingsDisabled}
					/>
					Include an optional action box for this question.
				</label>
			</div>
		</div>
	);
};

export default AdminCCEmailRecipientBlock;
